import { RotateCcw, Search, UserRoundPen } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from '@/lib/scopedParams';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { api } from '../../api';
import type { RegistrantChangeStatus, RegistrantChangeSummary } from '../../api/areas/core';
import { DataTable, Field, FilterBar, Pager, Section, type Column } from '../../components/blocks';
import { DataError } from '../../components/DataError';
import { EntityRef } from '../../components/EntityRef';
import { PageHeader, useDocumentTitle } from '../../components/PageHeader';
import { EmptyState, Loading } from '../../components/States';
import { formatDateTime } from '../../lib/format';
import { useApi } from '../../lib/useApi';
import { CustomerRef, SubjectRef } from '../domains/refs';
import { StatusBadge, TRANSFER, useStatusLabel } from '../domains/status';
import { nextParams } from './shared';

const LIMIT = 50;
const ANY = '__any';
const STATUSES: RegistrantChangeStatus[] = ['REQUESTED', 'AUTHORIZED', 'REGISTRY_PENDING', 'COMPLETED', 'REJECTED', 'CANCELLED', 'EXPIRED'];

/** `/registrant-changes`: requests to hand a domain to a new registrant. */
export function RegistrantChangesPage() {
  const { t } = useTranslation('domains');
  const { t: tc } = useTranslation();
  const statusLabel = useStatusLabel();
  useDocumentTitle(t('changes.title'));
  const [params, setParams] = useSearchParams();
  const domain = params.get('domain') ?? '';
  const status = (params.get('status') ?? '') as RegistrantChangeStatus | '';
  const before = params.get('before') ?? '';
  const [draft, setDraft] = useState(domain);
  const [draftFor, setDraftFor] = useState(domain);
  const [history, setHistory] = useState<string[]>([]);
  const result = useApi(
    (signal) => api.listRegistrantChanges({ domain: domain || undefined, status: status || undefined, before: before || undefined, limit: LIMIT }, { signal }),
    [domain, status, before],
  );
  if (draftFor !== domain) {
    setDraftFor(domain);
    setDraft(domain);
  }
  const update = (next: Record<string, string>) => setParams(nextParams(params, next));
  const submit = (e: FormEvent) => {
    e.preventDefault();
    setHistory([]);
    update({ domain: draft.trim().toLowerCase(), before: '' });
  };
  const filtered = Boolean(domain || status);
  const reset = () => {
    setHistory([]);
    setDraft('');
    update({ domain: '', status: '', before: '' });
  };

  const columns: Column<RegistrantChangeSummary>[] = [
    {
      key: 'domain',
      header: t('changes.colDomain'),
      cell: (x) => (
        <EntityRef
          label={x.unicode_name}
          to={`/registrant-changes/${encodeURIComponent(x.id)}`}
          id={x.id}
          sub={x.unicode_name !== x.ascii_name ? <span className="font-mono">{x.ascii_name}</span> : undefined}
        />
      ),
    },
    { key: 'from', header: t('changes.colFrom'), cell: (x) => <SubjectRef id={x.from_subject_id} name={x.from_display_name} type={x.from_subject_type} /> },
    {
      key: 'to',
      header: t('changes.colTo'),
      cell: (x) => (
        <div className="flex flex-col gap-1">
          <SubjectRef id={x.to_subject_id} name={x.to_display_name} type={x.to_subject_type} />
          {x.transfer_lock_opt_out ? <span className="text-xs text-muted-foreground">{t('changes.lockOptOut')}</span> : null}
        </div>
      ),
    },
    { key: 'status', header: t('changes.colStatus'), cell: (x) => <StatusBadge map={TRANSFER} value={x.status} /> },
    { key: 'customer', header: t('changes.colCustomer'), cell: (x) => <CustomerRef id={x.customer_id} name={x.customer_name} /> },
    { key: 'requested', header: t('changes.colRequested'), className: 'whitespace-nowrap', cell: (x) => formatDateTime(x.requested_at) },
  ];

  return (
    <>
      <PageHeader title={t('changes.title')} crumbs={[{ label: t('changes.crumb') }]} subtitle={t('changes.subtitle')} />
      <form onSubmit={submit} role="search">
        <FilterBar
          actions={
            filtered ? (
              <Button type="button" variant="ghost" size="sm" onClick={reset}>
                <RotateCcw /> {tc('action.reset')}
              </Button>
            ) : null
          }
        >
          <Field label={t('changes.filterDomain')} className="w-full sm:w-64">
            <div className="relative">
              <Search className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
              <Input type="search" value={draft} onChange={(e) => setDraft(e.target.value)} placeholder={t('changes.filterDomainPlaceholder')} autoComplete="off" spellCheck={false} className="bg-card pl-8" />
            </div>
          </Field>
          <Field label={t('changes.filterStatus')} className="w-full sm:w-52">
            <Select
              value={status || ANY}
              onValueChange={(v) => {
                setHistory([]);
                update({ status: v === ANY ? '' : v, before: '' });
              }}
            >
              <SelectTrigger className="w-full bg-card">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ANY}>{t('changes.anyStatus')}</SelectItem>
                {STATUSES.map((s) => (
                  <SelectItem key={s} value={s}>
                    {statusLabel(TRANSFER, s)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Button type="submit">
            <Search /> {t('changes.search')}
          </Button>
        </FilterBar>
      </form>

      <Section flush title={t('changes.tableTitle')} description={t('changes.tableDescription')}>
        {result.status === 'loading' ? (
          <div className="px-5">
            <Loading rows={4} />
          </div>
        ) : null}
        {result.status === 'error' ? (
          <div className="px-5 pb-5">
            <DataError error={result.error} onRetry={result.reload} />
          </div>
        ) : null}
        {result.status === 'success' ? (
          <>
            <DataTable
              columns={columns}
              rows={result.data.items}
              rowKey={(x) => x.id}
              empty={
                <EmptyState title={t('changes.empty')} icon={UserRoundPen}>
                  {t('changes.emptyBody')}
                </EmptyState>
              }
            />
            {result.data.items.length > 0 || before ? (
              <Pager
                canPrevious={Boolean(before)}
                canNext={Boolean(result.data.next_before)}
                onPrevious={() => {
                  const prev = history.slice(0, -1);
                  update({ before: history[history.length - 1] ?? '' });
                  setHistory(prev);
                }}
                onNext={() => {
                  setHistory([...history, before]);
                  update({ before: result.data.next_before ?? '' });
                }}
                info={t('changes.shown', { count: result.data.items.length })}
              />
            ) : null}
          </>
        ) : null}
      </Section>
    </>
  );
}
